import { zodResolver } from "@hookform/resolvers/zod";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { useAccount } from "wagmi";

import { z } from "zod";
import Button from "../Button";
import type { ProductDeal } from "./BlockchainPayment";
import PaymentConfirmation from "./PaymentConfirmation";
import PaymentInput from "./PaymentInput";
import usePrices from "./hooks/usePrices";
import useSubmitForm from "./hooks/useSubmitForm";
import { useStore } from "./store";

const paymentFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email"),
  telegramId: z.string().optional(),
  mobilePhone: z.string().optional(),
});

export type PaymentFormValues = z.infer<typeof paymentFormSchema>;

const PaymentForm = ({ productDeal }: { productDeal: ProductDeal }) => {
  const isFullPrice = productDeal === "full-price";
  const paymentMethod: "eth" | "kolz" = isFullPrice ? "eth" : "kolz";

  const router = useRouter();
  const { transactionId, isProcessing, reset: resetStoreState } = useStore();
  const { ethPrice, halfEthInKolz, isError: isErrorPrices, isLoading: isLoadingPrices } = usePrices();
  const { address, isConnecting } = useAccount();

  useEffect(() => {
    if (!address && !isConnecting) {
      router.replace("/pricing");
    }
  }, [address, isConnecting, router]);

  useEffect(
    () => () => {
      resetStoreState();
    },
    [resetStoreState],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      telegramId: "",
      mobilePhone: "",
    },
  });

  const { onSubmit } = useSubmitForm(paymentMethod, reset);

  if (isLoadingPrices || isConnecting) {
    return (
      <div className="flex flex-col items-center justify-center h-[500px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (isErrorPrices) {
    return (
      <div className="flex flex-col items-center justify-center h-[500px] p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">
            We couldn&apos;t fetch the current ETH and KOLZ prices.{" "}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="underline font-medium hover:text-red-800"
            >
              Click here to refresh
            </button>
          </span>
        </div>
      </div>
    );
  }

  if (transactionId) {
    return <PaymentConfirmation />;
  }

  return (
    <>
      <div className="mb-8">
        <h3 className="text-2xl font-semibold mb-4 text-white">Complete Your Purchase</h3>
        <p className="text-white/80 mb-4">Enter your contact details and wallet address to begin AI Agent setup.</p>
        <ul className="list-disc list-inside text-white/70 mb-4 space-y-2">
          <li>Contact info ensures a smooth onboarding process.</li>
          <li>Your wallet verifies payment and enables integration.</li>
        </ul>
        <p className="text-sm text-white/60 italic">Your data is 100% confidential and will never be shared.</p>
      </div>
      <div className="opacity-0 translate-x-[-100%] animate-slide-in rounded-xl py-6 px-6 md:px-9 w-full max-w-xl bg-white text-black">
        <form className="flex flex-col items-center gap-3" onSubmit={handleSubmit(onSubmit)}>
          <PaymentInput
            label="Wallet Address"
            type="text"
            id="walletAddress"
            placeholder="Wallet Address"
            value={`${address?.substring(0, 6)}...${address?.substring(address.length - 4, address.length)}`}
            required
            disabled
          />
          <PaymentInput
            label="First Name"
            type="text"
            {...register("firstName", { required: "First name is required" })}
            error={errors.firstName}
          />
          <PaymentInput
            label="Last Name"
            type="text"
            {...register("lastName", { required: "Last name is required" })}
            error={errors.lastName}
          />
          <PaymentInput
            label="Email"
            type="email"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email",
              },
            })}
            error={errors.email}
          />
          <PaymentInput label="Telegram Username" type="text" {...register("telegramId")} error={errors.telegramId} />

          <div className="border-b border-black pb-3 w-full mt-6 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p>Price</p>
              <div className="flex items-center gap-2">
                1.00
                <Image src="/eth.svg" alt="eth" height={12} width={12} />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p>
                Discount
                {isFullPrice && (
                  <span className="text-sm text-gray-500">
                    {" "}
                    (Switch to{" "}
                    <Link href="/pricing?deal=half-price" className="underline">
                      $KOLZ payment
                    </Link>{" "}
                    for 50% discount)
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2">
                {!isFullPrice ? "-0.50" : "-0.00"}
                <Image src="/eth.svg" alt="eth" height={12} width={12} />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between w-full font-semibold mb-3">
            <p>Total Price</p>
            <div className="flex items-center gap-2">
              {isFullPrice ? "1.00" : "0.50"} ($
              {isFullPrice ? ethPrice.toFixed(2) : (ethPrice / 2).toFixed(2)})
              <Image src="/eth.svg" alt="eth" height={12} width={12} />
            </div>
          </div>

          {!isFullPrice && (
            <div className="flex items-center justify-between w-full mb-3">
              <p>KOLZ Amount</p>
              <div className="flex items-center gap-2">{Math.floor(halfEthInKolz).toLocaleString()} $KOLZ</div>
            </div>
          )}

          <Button
            type="submit"
            isLoading={isProcessing}
            disabled={!isValid || !address || isProcessing}
            className={twMerge(
              "rounded-lg w-full flex justify-center gap-2 px-3 py-3",
              !isValid || !address ? "cursor-not-allowed opacity-40" : "active:scale-95",
            )}
          >
            Pay with {paymentMethod === "eth" ? "ETH" : "$KOLZ"}
          </Button>
        </form>
      </div>
      <p className="text-sm text-white/60 mt-4">
        <Link href="/pricing" className="underline">
          Return to pricing page
        </Link>
      </p>
    </>
  );
};

export default PaymentForm;
